// ═══════════════════════════════════════════════════════════════════════════
// routing.js — single source of truth for view↔path mapping (mini-spec #10).
//
// The app's nav system used to be hash-based (`/#explore`). This file
// flips it to path-based (`/explore`) while keeping the internal
// `view` state machine unchanged — `navigate(view)` is still the
// public API; it just writes the path now instead of the hash.
//
// Backward-compat: any URL arriving with a legacy `#view` hash gets
// rewritten to the clean path on mount via `normalizeLegacyHash()`.
// Old email links (from #7 morning briefings) and bookmarks keep
// working forever — they redirect once, then live at the clean URL.
// ═══════════════════════════════════════════════════════════════════════════

// view → public URL path. Order doesn't matter; lookups are O(1) via the
// reverse map below. Add new views here and `pathFor` / `viewFromPath`
// pick them up automatically.
const VIEW_TO_PATH = {
  shows:           '/',                  // home
  explore:         '/explore',
  search:          '/search',
  builder:         '/create',
  clips:           '/clips',           // Brand normalized back to "Clips" — see WorkspaceSidebar comment for the label evolution.
  profile:         '/profile',
  episodes:        '/library',
  inbox:           '/messages',         // Renamed in #12 — internal view name kept as 'inbox'
  leaderboard:     '/leaderboard',
  'shows-manager': '/manage-shows',
  'show-editor':   '/manage-shows',      // path is /manage-shows/<id> — id appended at write time
  account:         '/account',
  subscription:    '/subscription',
  billing:         '/billing',
  settings:        '/settings',
  pricing:         '/pricing',
  admin:           '/admin',
  labs:            '/labs',
  credits:         '/credits',
  invite:          '/invite',
  workspaces:      '/workspaces',
  'clip-upload':   '/clip-upload',
  dashboard:       '/dashboard',
  marketplace:     '/partners',         // legacy alias → canonical /partners (#38)
  partners:        '/partners',
  matches:         '/matches',
  // "Guesting" is a top-level rail item. Without an entry here pathFor fell
  // through to '/', so navigating to it silently rewrote the URL to home —
  // refresh / share / back-button all dumped the user on the home page.
  collabs:         '/guesting',
  // Linked from GuestHome ("Complete your guest profile"). Both render the
  // profile page; the reverse map keys off the first segment, so
  // /guest-profile/edit resolves back to 'guest-profile' — same surface.
  'guest-profile':      '/guest-profile',
  'guest-profile-edit': '/guest-profile/edit',
  analytics:       '/analytics',
  brief:           '/brief',
  editor:          '/editor',
  // /short-form was a standalone page; short-form scripts are now
  // generated inside the chat on /create (Short-form tab). The view +
  // path are gone; LEGACY_PATH_REDIRECTS below redirects any lingering
  // bookmark to /create.
  arcs:            '/arcs',              // Arcs feature landing page
  help:            '/help',
  support:         '/support',
};

// Reverse: leading path segment → view name. We compare the FIRST
// segment only so that `/manage-shows/<uuid>` resolves to `show-editor`
// and the uuid is parsed separately.
const PATH_TO_VIEW = {};
for (const [view, path] of Object.entries(VIEW_TO_PATH)) {
  // Skip the show-editor alias so /manage-shows resolves to the manager,
  // not the editor; the editor is reached via the second-segment uuid.
  if (view === 'show-editor') continue;
  const seg = path === '/' ? '' : path.replace(/^\//, '');
  if (!(seg in PATH_TO_VIEW)) PATH_TO_VIEW[seg] = view;
}

// Legacy redirects — old URLs still resolve so user bookmarks and
// shared links keep working after a rename. The path resolver
// normalizes them to the canonical URL.
const LEGACY_PATH_REDIRECTS = {
  inbox:  '/messages',
  // The clips surface has cycled labels — at one point we shipped
  // /slices as the canonical URL. Redirect any lingering bookmark
  // back to the canonical /clips.
  slices: '/clips',
  // Short-form scripts moved INTO the chat (Short-form tab on /create);
  // the standalone /short-form page was removed. Old bookmarks (and the
  // refresh-on-clips bug that was sending users here) now land on /create.
  'short-form': '/create',
};

/** Build the full URL path for a navigate() call. */
export function pathFor(view, opts = {}) {
  if (view === 'show-editor' && opts.showId) return `/manage-shows/${opts.showId}`;
  if (view === 'editor' && opts.episodeId)   return `/editor/${opts.episodeId}`;
  // An unmapped view silently becomes '/', which is how "Guesting" shipped
  // with a home-page URL. Shout in dev so the next missing route is caught
  // at the keyboard instead of in production.
  if (!VIEW_TO_PATH[view] && process.env.NODE_ENV !== 'production') {
    console.warn(`[routing] No path mapped for view "${view}" — falling back to "/". Add it to VIEW_TO_PATH.`);
  }
  return VIEW_TO_PATH[view] || '/';
}

/**
 * Resolve a `location` (anything with .pathname) into a view name + extras.
 * Returns null when the path doesn't map to any known view — the caller
 * should leave the current view untouched in that case (the path may be
 * `/@<handle>` which has its own handler in EpisodeBuilderApp).
 */
export function viewFromPath(location) {
  const path = (location && location.pathname) || '/';

  // /@<handle> is its own beast — never resolve it as a view here.
  if (/^\/@/.test(path)) return null;

  // Root → home (which is the 'shows' view).
  if (path === '/' || path === '') return { view: 'shows' };

  const segments = path.replace(/^\/+/, '').split('/');
  const head     = segments[0];

  // Legacy URL redirect (mini-spec #12): old `/inbox` → `/messages`,
  // `/slices` → `/clips`. Tell the caller to replaceState the
  // canonical path along with the resolved view.
  if (head in LEGACY_PATH_REDIRECTS) {
    const target = LEGACY_PATH_REDIRECTS[head];
    const targetSeg = target.replace(/^\//, '');
    return { view: PATH_TO_VIEW[targetSeg], redirectTo: target };
  }

  // /manage-shows/<id> → show-editor with the id captured.
  if (head === 'manage-shows' && segments[1]) {
    return { view: 'show-editor', showId: segments[1] };
  }
  if (head === 'manage-shows') {
    return { view: 'shows-manager' };
  }

  // /editor/<id> → editor with the episode id captured so a refresh
  // can re-fetch the episode and re-render the editor surface.
  if (head === 'editor' && segments[1]) {
    return { view: 'editor', episodeId: segments[1] };
  }

  const view = PATH_TO_VIEW[head];
  return view ? { view } : null;
}

/**
 * One-time legacy hash redirect. If the URL arrived with `#view-name`,
 * rewrite it to `/view-name` and return the resolved view. Returns null
 * when there's no hash to normalize.
 *
 * Preserves any query string from the hash form (e.g. `#brief?topic=x`
 * → `/brief?topic=x`).
 */
export function normalizeLegacyHash() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  if (!hash) return null;
  const raw = hash.replace(/^#/, '');
  if (!raw) return null;
  const [namePart, queryPart = ''] = raw.split('?');
  if (!(namePart in PATH_TO_VIEW)) return null;
  const view  = PATH_TO_VIEW[namePart];
  const path  = pathFor(view);
  const url   = path + (queryPart ? `?${queryPart}` : '');
  try { window.history.replaceState(null, '', url); } catch {}
  return { view };
}
